import { Box, Grid, Stack, styled, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { t } from "i18next";

import { useAppSelector } from "~/app/store";
import { ActivityState } from "~/features/autostoreBin";
import { AutostoreBinWithAnimation } from "~/features/autostoreBin/AutostoreBinWithAnimation";
import { usePortStatusQuery } from "~/hooks/usePortStatus";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import {
  selectBinIsPresent,
  selectLeftOrRightPortActive,
  selectPickingState,
  selectShowTwoBinComponents
} from "~/redux/selectors/autostoreSelectors";
import { selectPickQuantityForDisplay } from "~/redux/selectors/outOfStockSelectors";
import {
  selectIsShortSideBin,
  selectWorkstationAvailablePorts
} from "~/redux/selectors/workstationsSelectors";
import { useGetInventoryByIdQuery } from "~/redux/warehouse/inventory.hooks";
import { PickDto } from "~/types/api";

import { selectPickCompartment } from "./autostorePicking.slice";

const TwoBinContainer = styled(Grid)`
  display: flex;
  width: 150%;
  position: relative;
  left: -25%;
  margin-bottom: 30px;
`;

type Props = {
  currentPick: PickDto | null;
};

export default function PickingBins({ currentPick }: Props) {
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const binIsPresent = useAppSelector(selectBinIsPresent);
  const showTwoBinComponents = useAppSelector(selectShowTwoBinComponents);
  const pickingState = useAppSelector(selectPickingState);
  const leftOrRightPortActive = useAppSelector(selectLeftOrRightPortActive);
  const pickQuantityForDisplay = useAppSelector(selectPickQuantityForDisplay);
  const pickCompartment = useAppSelector(selectPickCompartment);
  const [portId] = useAppSelector(selectWorkstationAvailablePorts);
  const { isReady, isBinOpen } = usePortStatusQuery(portId);
  const isBinReady = isReady || isBinOpen;

  const { ap_fusionPortScreenEnabled, ap_showPickQtyWhileBinLoaderIsActive } =
    useAppSelector(selectServerConfig);
  const displayTwoBinComponents =
    ap_fusionPortScreenEnabled && showTwoBinComponents;

  const { data: inventory } = useGetInventoryByIdQuery(
    currentPick ? { inventoryId: currentPick.inventoryId } : skipToken,
    { refetchOnMountOrArgChange: true }
  );
  const isInventoryOnHold = !!inventory?.holds?.length;
  const isBinFlagged = inventory?.bin?.autostoreBin?.isFlagged;

  const numberOfColumns =
    pickingState?.binConfiguration?.verticalCompartmentCount;
  const numberOfRows =
    pickingState?.binConfiguration?.horizontalCompartmentCount;

  const binNumber = currentPick?.pickBin?.autostoreBin?.autostoreBinId;

  const getBinIdForDisplay = () =>
    isBinFlagged
      ? ` ${t("bin is flagged", { binNumber })}`
      : `${t("bin")} ${binNumber}`;

  /** Overrides the default bin animation state in specific cases */
  const getBinStateOverride = (
    isActive: boolean
  ): ActivityState | undefined => {
    if (displayTwoBinComponents && isBinReady && !isActive) {
      return "Inactive";
    }
    return undefined;
  };

  return (
    <>
      {displayTwoBinComponents ? (
        <TwoBinContainer>
          {(["Left", "Right"] as const).map((side) => {
            const isActive = side === leftOrRightPortActive;
            const showBinId =
              !!binNumber && (!isBinReady || (isBinReady && isActive));
            return (
              <Stack
                key={side}
                alignItems="center"
                flexGrow={1}
                // Todo: refactor the parent elements' styling to remove this (WMP-2165).
                // Without this maxWidth, the bins shift position between states
                maxWidth="49%"
                height={isShortSideBin ? "300px" : "unset"}
              >
                <AutostoreBinWithAnimation
                  portId={portId}
                  stateOverride={getBinStateOverride(isActive)}
                  numberOfColumns={(isActive && numberOfColumns) || 1}
                  numberOfRows={(isActive && numberOfRows) || 1}
                  isBinFlagged={isActive ? isBinFlagged : undefined}
                  isSelectedCompartmentInventoryOnHold={
                    isActive ? isInventoryOnHold : undefined
                  }
                  pickCompartment={pickCompartment}
                  pickQuantity={pickQuantityForDisplay}
                  binId={binNumber}
                  showPickQtyWhileBinLoaderIsActive={
                    ap_showPickQtyWhileBinLoaderIsActive
                  }
                  hideBinId
                  isShortSideBin={isShortSideBin}
                />

                {showBinId && (
                  <Box style={{ height: 25 }}>
                    <Typography variant="h5" textAlign="center">
                      {getBinIdForDisplay()}
                    </Typography>
                  </Box>
                )}
              </Stack>
            );
          })}
        </TwoBinContainer>
      ) : (
        <Stack alignItems="center" height={isShortSideBin ? "300px" : "unset"}>
          <AutostoreBinWithAnimation
            portId={portId}
            stateOverride={getBinStateOverride(true)}
            numberOfColumns={numberOfColumns || 1}
            numberOfRows={numberOfRows || 1}
            pickCompartment={pickCompartment}
            pickQuantity={pickQuantityForDisplay}
            binId={binNumber}
            showPickQtyWhileBinLoaderIsActive={
              ap_showPickQtyWhileBinLoaderIsActive
            }
            isBinFlagged={isBinFlagged}
            isSelectedCompartmentInventoryOnHold={isInventoryOnHold}
            hideBinId
            isShortSideBin={isShortSideBin}
          />
        </Stack>
      )}
      <Box style={{ height: 25 }}>
        {!!binNumber && !ap_fusionPortScreenEnabled && binIsPresent && (
          <Typography variant="h5" textAlign="center">
            {getBinIdForDisplay()}
          </Typography>
        )}
      </Box>
    </>
  );
}
